import { Autocomplete, Box, TextField } from "@mui/material";
import React, { SyntheticEvent, useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../hooks/hooks";
import { getBranchesDepartments } from "../../../store/thunks/portalManagementThunk";
import {
  setSelectedBranch,
  setSelectedDepartment,
} from "../../../store/features/portalManagement/portalManagementSlice";

// "Overall" is the default rollup and always sorts first, ahead of the raw list.
const overallFirst = (options: any[] | undefined) =>
  options
    ? [
        ...options.filter((o: any) => o.value === "Overall"),
        ...options.filter((o: any) => o.value !== "Overall"),
      ]
    : [];

const fieldSx = { width: 190 };

/**
 * The branch/department selects lifted out of dashboardHeader.tsx so they can be
 * passed to <ContextBar filters={...}>. Owns the getBranchesDepartments dispatch
 * that dashboardHeader used to fire on mount, so the options still load.
 */
/**
 * Loads and orders the branch/department options, and exposes the two setters.
 * Shared so the legacy variant differs only in markup — see
 * commonComponents/appShell/legacy/legacyFilters.tsx.
 */
export const useBranchDepartmentFilters = () => {
  const dispatch = useAppDispatch();

  const {
    selectedDepartment,
    selectedBranch,
    selectedClient,
    selectedAccountType,
    branches,
    departments,
  } = useAppSelector((state: any) => state.portalManagement);
  const { user } = useAppSelector((state: any) => state.authentication);

  const [branchOptions, setBranchOptions] = useState<any[]>([]);
  const [departmentOptions, setDepartmentOptions] = useState<any[]>([]);

  const handleBranchChange = (selection: any) => {
    dispatch(setSelectedBranch(selection));
  };
  const handleDepartmentChange = (selection: any) => {
    dispatch(setSelectedDepartment(selection));
  };

  // Latest-ref: the request is fired once on mount, but with the values current
  // at that moment — not the ones captured when the effect was first created.
  const payloadRef = useRef({
    portal_account: selectedAccountType,
    customer_id: selectedClient?.customer_id,
    user: user?.role,
  });
  payloadRef.current = {
    portal_account: selectedAccountType,
    customer_id: selectedClient?.customer_id,
    user: user?.role,
  };

  useEffect(() => {
    dispatch(getBranchesDepartments({ payload: payloadRef.current }));
  }, [dispatch]);

  useEffect(() => {
    setBranchOptions(overallFirst(branches));
    setDepartmentOptions(overallFirst(departments));
  }, [branches, departments]);

  return {
    selectedBranch,
    selectedDepartment,
    branchOptions,
    departmentOptions,
    handleBranchChange,
    handleDepartmentChange
  };
};

const DashboardFilters = () => {
  const {
    selectedBranch,
    selectedDepartment,
    branchOptions,
    departmentOptions,
    handleBranchChange,
    handleDepartmentChange
  } = useBranchDepartmentFilters();

  return (
    <Box sx={{ display: "flex", gap: 1.5 }}>
      <Autocomplete
        id="branch"
        size="small"
        sx={fieldSx}
        value={selectedBranch}
        onChange={(_: SyntheticEvent, newValue: string | null) =>
          handleBranchChange(newValue)
        }
        options={branchOptions}
        isOptionEqualToValue={(option, newValue) => option.id === newValue.id}
        renderInput={(params) => <TextField {...params} label="Branches" />}
      />
      <Autocomplete
        id="department"
        size="small"
        sx={fieldSx}
        value={selectedDepartment}
        onChange={(_: SyntheticEvent, newValue: string | null) =>
          handleDepartmentChange(newValue)
        }
        options={departmentOptions}
        isOptionEqualToValue={(option, newValue) => option.id === newValue.id}
        renderInput={(params) => <TextField {...params} label="Departments" />}
      />
    </Box>
  );
};

export default DashboardFilters;
